import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import {
  X,
  Link2,
  Star,
  CalendarDays,
  ChevronDown,
  AlertTriangle,
  GraduationCap,
  Clock,
  MapPin,
  BarChart3,
  Users,
  CheckCircle2,
  CalendarPlus,
  Lightbulb,
  Sparkles,
} from 'lucide-react';
import { getAcademyTraining, boekTraining, downloadAcademyAgenda, zetOpWachtlijst, haalVanWachtlijst } from '../../services/medewerkerApi';
import type { AcademyInschrijving, AcademySessie, AcademyTraining, AcademyTrainingDetail, Wallet } from '../../services/medewerkerApi';
import { AcademyInschrijfModal } from './AcademyInschrijfModal';
import { ACCENT_KLEUR, LEERVORM, WKR_ORANJE, accentVoor, datumLang, eur, prijs, tijd, verdeling, wkrGrendelActief } from './academyStijl';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

const Tekstblok: React.FC<{ titel: string; tekst: string | null | undefined }> = ({ titel, tekst }) =>
  tekst?.trim() ? (
    <section>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{titel}</h3>
      {/* Opleider-invoer is platte tekst (textarea): nooit als HTML renderen. */}
      <p className="mt-1.5 whitespace-pre-line text-[15px] leading-relaxed text-gray-700">{tekst.trim()}</p>
    </section>
  ) : null;

const ReviewBalk: React.FC<{ label: string; waarde: number }> = ({ label, waarde }) => (
  <div>
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <span className="inline-flex items-center gap-1 font-semibold text-gray-900">
        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
        {waarde.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
      </span>
    </div>
    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuemin={1} aria-valuemax={5} aria-valuenow={waarde} aria-label={label}>
      <div className="h-full rounded-full bg-amber-400" style={{ width: `${(waarde / 5) * 100}%` }} />
    </div>
  </div>
);

/** Kalender-slot: alleen de startdatum is klikbaar; de vervolgdata klappen uit als read-only tijdlijn. */
const SessieSlot: React.FC<{ sessie: AcademySessie; gekozen: boolean; onKies: () => void }> = ({ sessie, gekozen, onKies }) => {
  const { t } = useTranslation('medewerker');
  const [open, setOpen] = useState(false);
  const boekbaar = sessie.status === 'AVAILABLE' && sessie.vrij > 0;
  const naam = sessie.naam || t('academy.detail.sessie', { defaultValue: 'Sessie' });
  const n = sessie.lessen.length;

  return (
    <li
      className={`rounded-xl border p-3 transition ${
        !boekbaar
          ? 'border-red-200 bg-red-50'
          : gekozen
            ? 'border-emerald-500 bg-emerald-50'
            : 'border-gray-200 bg-white hover:border-gray-400'
      }`}
    >
      <label className={`flex items-start gap-2.5 ${boekbaar ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
        <input
          type="radio"
          name="academy-sessie"
          checked={gekozen}
          disabled={!boekbaar}
          onChange={onKies}
          className="mt-0.5 h-4 w-4 accent-emerald-500"
        />
        <span className="min-w-0 flex-1 text-sm">
          <span className={`font-semibold ${boekbaar ? 'text-gray-900' : 'text-gray-400'}`}>{naam}:</span>{' '}
          <span className={boekbaar ? 'text-gray-700' : 'text-gray-400'}>
            {sessie.start_at
              ? t('academy.detail.startOp', { defaultValue: 'Start op {{datum}}', datum: datumLang(sessie.start_at) })
              : '—'}
          </span>
          <span className="mt-0.5 block text-xs">
            {sessie.status === 'EXPIRED' ? (
              <span className="font-medium text-red-600">❌ {t('academy.detail.verlopen', { defaultValue: 'Verlopen' })}</span>
            ) : !boekbaar ? (
              <span className="font-medium text-red-600">🔴 {t('academy.detail.vol', { defaultValue: 'VOL' })}</span>
            ) : (
              <span className={sessie.vrij <= 5 ? 'font-medium text-amber-700' : 'text-emerald-700'}>
                {sessie.vrij === 1
                  ? t('academy.detail.nogEenVrij', { defaultValue: 'Nog 1 plek vrij' })
                  : t('academy.detail.nogVrij', { defaultValue: 'Nog {{n}} plekken vrij', n: sessie.vrij })}
              </span>
            )}
          </span>
        </span>
      </label>

      {n > 1 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="ml-6 mt-2 inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-800"
          >
            <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
            {t('academy.detail.toonLesdata', { defaultValue: 'Toon alle {{n}} lesdata', n })}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
          <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
            <div className="overflow-hidden">
            <ol className="ml-7 border-l border-gray-200" aria-label={t('academy.detail.lesschema', { defaultValue: 'Lesschema' })}>
              {sessie.lessen.map((les) => (
                <li key={les.volgnummer} className="relative py-1.5 pl-4 text-xs text-gray-600 first:mt-2 last:mb-1">
                  <span
                    className={`absolute -left-[5px] top-2.5 h-2.5 w-2.5 rounded-full ${les.volgnummer === 1 ? 'bg-emerald-500' : 'bg-gray-400'}`}
                    aria-hidden="true"
                  />
                  <span className="font-medium text-gray-900">
                    {les.volgnummer === 1
                      ? t('academy.detail.lesStart', { defaultValue: 'Les 1 (Start)' })
                      : t('academy.detail.lesN', { defaultValue: 'Les {{n}}', n: les.volgnummer })}
                    :
                  </span>{' '}
                  📅 {datumLang(les.start_at)} | {tijd(les.start_at)} - {tijd(les.eind_at)} {t('academy.detail.uur', { defaultValue: 'uur' })}
                  {les.locatie && <span className="block text-gray-500">{les.locatie}</span>}
                </li>
              ))}
            </ol>
            </div>
          </div>
        </>
      )}
    </li>
  );
};

/**
 * Informatie-scherm (Netflix detail-overlay): links (70%) beeld, omschrijving,
 * leerdoelen en reviewbalken; rechts (30%) de transparante financiële breakdown,
 * datum-slot-keuze en de inkoopknop.
 */
export const AcademyDetailModal: React.FC<{
  training: AcademyTraining;
  wallet: Wallet | undefined;
  onClose: () => void;
}> = ({ training: basis, wallet, onClose }) => {
  const { t } = useTranslation('medewerker');
  const queryClient = useQueryClient();
  const sluitKnop = useRef<HTMLButtonElement>(null);
  const [sessieId, setSessieId] = useState<number | null>(null);
  const [geboekt, setGeboekt] = useState<{ boekingId: string; metAgenda: boolean } | null>(null);
  // Inschrijf-wrapper: tussenstap waarin de medewerker zijn gegevens voor de opleider invult.
  const [inschrijven, setInschrijven] = useState(false);

  const detailQuery = useQuery({ queryKey: ['medewerker-academy-training', basis.id], queryFn: () => getAcademyTraining(basis.id) });
  const detail: AcademyTrainingDetail | undefined = detailQuery.data;
  const tr: AcademyTraining = detail ?? basis;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    sluitKnop.current?.focus();
    const vorig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const toets = (e: KeyboardEvent) => e.key === 'Escape' && onCloseRef.current();
    window.addEventListener('keydown', toets);
    return () => {
      document.body.style.overflow = vorig;
      window.removeEventListener('keydown', toets);
    };
  }, []);

  const boekMutation = useMutation({
    mutationFn: (inschrijving: AcademyInschrijving) => boekTraining(tr.id, sessieId, inschrijving),
    onSuccess: (res) => {
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      setInschrijven(false);
      setGeboekt({ boekingId: res.boeking_id, metAgenda: Boolean(res.sessie_id) });
      queryClient.invalidateQueries({ queryKey: ['medewerker-academy'] });
      queryClient.invalidateQueries({ queryKey: ['medewerker-academy-training', tr.id] });
    },
    onError: () => queryClient.invalidateQueries({ queryKey: ['medewerker-academy-training', tr.id] }),
  });
  const agendaMutation = useMutation({ mutationFn: (id: string) => downloadAcademyAgenda(id, tr.titel) });
  // Wachtlijst ("Houd mij op de hoogte van nieuwe data") op een volgeboekte of datumloze cursus.
  const wachtlijstMutation = useMutation({
    mutationFn: (aan: boolean) => (aan ? zetOpWachtlijst(tr.id).then(() => undefined) : haalVanWachtlijst(tr.id)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['medewerker-academy-training', tr.id] }),
  });

  const accent = ACCENT_KLEUR[accentVoor(tr)];
  const vorm = LEERVORM[tr.leervorm];
  const VormIcon = vorm.icon;
  const { walletDeel, bijbetaling } = verdeling(tr.netto_bedrag, wallet, tr.wkr_label);
  const sessies = detail?.sessies ?? [];
  const heeftSessies = tr.beschikbaarheid.heeft_sessies;
  const boekbareSessies = sessies.filter((s) => s.status === 'AVAILABLE' && s.vrij > 0);
  const wkrGrendel = wkrGrendelActief(tr, wallet);
  const sessieVerplicht = heeftSessies && sessieId === null;
  const kanBoeken = Boolean(wallet?.actief) && !sessieVerplicht && !(heeftSessies && boekbareSessies.length === 0) && Boolean(detail);

  const knopTekst = !wallet?.actief
    ? t('academy.nogNietBoeken', { defaultValue: 'Boeken nog niet mogelijk' })
    : heeftSessies && boekbareSessies.length === 0
      ? t('academy.detail.geenDatum', { defaultValue: 'Wacht op nieuwe datum' })
      : sessieVerplicht
        ? t('academy.detail.kiesDatum', { defaultValue: 'Kies eerst een startdatum' })
        : bijbetaling > 0
          ? t('academy.rekenAf', { defaultValue: '💳 Reken af: betaal {{bedrag}} anoniem bij via iDEAL', bedrag: eur.format(bijbetaling) })
          : t('academy.detail.activeer', { defaultValue: '🎓 Activeer Cursus met Mijn Wallet' });

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-slate-900/40 backdrop-blur-sm" onClick={() => !boekMutation.isPending && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="academy-detail-titel"
        className="relative mx-auto my-0 min-h-full w-full max-w-6xl overflow-hidden bg-gray-50 text-gray-700 shadow-2xl ring-1 ring-gray-200 sm:my-8 sm:min-h-0 sm:rounded-2xl"
        style={{ boxShadow: `0 0 0 1px ${accent}40, 0 25px 50px -12px rgba(10,25,47,.35)` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Beeld */}
        <div className="relative aspect-video max-h-[26rem] w-full overflow-hidden bg-[#10151f] sm:aspect-[21/8]">
          {tr.omslagfoto_url ? (
            <img src={tr.omslagfoto_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center text-8xl"
              style={{ background: `radial-gradient(120% 90% at 30% 20%, ${accent}55 0%, #0b1020 60%, #05070c 100%)` }}
              aria-hidden="true"
            >
              {tr.subcategorie_emoji || tr.categorie_emoji}
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/85 via-[#0A192F]/30 to-transparent" aria-hidden="true" />
          <button
            ref={sluitKnop}
            type="button"
            onClick={onClose}
            aria-label={t('algemeen.sluiten', { defaultValue: 'Sluiten' })}
            className="absolute right-3 top-3 rounded-full bg-black/70 p-2 text-white backdrop-blur hover:bg-black"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="absolute inset-x-0 bottom-0 px-5 pb-4 sm:px-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-white ${vorm.badge}`}>
                <VormIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {t(`academy.leervorm.${tr.leervorm}`, { defaultValue: vorm.label })}
              </span>
              {tr.rating && (
                <span className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1 text-xs font-bold text-white">
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
                  {tr.rating.gemiddelde.toLocaleString('nl-NL', { minimumFractionDigits: 1 })}
                  <span className="font-normal text-slate-400">({tr.rating.aantal})</span>
                </span>
              )}
              {tr.populair && (
                <span className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1 text-xs font-semibold text-orange-300">
                  🔥 {t('academy.tegel.populair', { defaultValue: 'Populair' })}
                </span>
              )}
              {tr.geadviseerd && (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/50 bg-black/70 px-2.5 py-1 text-xs font-semibold text-amber-200">
                  ⚕️ {t('academy.tegel.geadviseerd', { defaultValue: 'Geadviseerd door uw casemanager' })}
                </span>
              )}
            </div>
            <h2 id="academy-detail-titel" className="mt-2 text-2xl font-extrabold leading-tight text-white sm:text-4xl">
              {tr.titel}
            </h2>
          </div>
        </div>

        <div className="grid gap-8 px-5 pb-8 pt-4 sm:px-8 lg:grid-cols-[7fr_3fr]">
          {/* Linkerkant (70%) */}
          <div className="min-w-0 space-y-6">
            {tr.pitch && (
              <p className="flex items-start gap-2 text-lg font-semibold leading-snug text-gray-900">
                <Lightbulb className="mt-1 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
                {tr.pitch}
              </p>
            )}

            {detailQuery.isLoading && <p className="text-sm text-gray-500">…</p>}
            {detailQuery.isError && (
              <p className="text-sm text-red-600" role="alert">
                {foutMelding(detailQuery.error, t('academy.detail.laadfout', { defaultValue: 'De details konden niet worden geladen.' }))}
              </p>
            )}

            <Tekstblok titel={t('academy.detail.omschrijving', { defaultValue: 'Omschrijving' })} tekst={tr.samenvatting} />
            <Tekstblok titel={t('academy.detail.leerdoelen', { defaultValue: 'Leerdoelen' })} tekst={detail?.leerdoelen} />
            <Tekstblok titel={t('academy.detail.programma', { defaultValue: 'Programma' })} tekst={detail?.programma_onderdelen} />
            <Tekstblok titel={t('academy.detail.voorWie', { defaultValue: 'Voor wie' })} tekst={detail?.voor_wie || detail?.doelgroep} />

            {detail && (
              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  {t('academy.detail.reviews', { defaultValue: 'Beoordelingen' })}
                </h3>
                {detail.reviews.balken ? (
                  <div className="mt-2 grid gap-3 sm:grid-cols-3">
                    <ReviewBalk label={t('academy.detail.relevantie', { defaultValue: 'Relevantie voor je werk' })} waarde={detail.reviews.balken.relevantie} />
                    <ReviewBalk label={t('academy.detail.toepasbaarheid', { defaultValue: 'Direct toepasbaar' })} waarde={detail.reviews.balken.toepasbaarheid} />
                    <ReviewBalk label={t('academy.detail.expertise', { defaultValue: 'Expertise docent' })} waarde={detail.reviews.balken.expertise} />
                  </div>
                ) : (
                  <p className="mt-1.5 text-sm text-gray-500">
                    {t('academy.detail.nogGeenReviews', {
                      defaultValue: 'Scores worden zichtbaar vanaf 5 beoordelingen ({{n}} tot nu toe).',
                      n: detail.reviews.aantal,
                    })}
                  </p>
                )}
              </section>
            )}

            {/* Metadata-kolom (Laag B: aanbieder als groene, klikbare link) */}
            <dl className="grid gap-x-6 gap-y-3 rounded-xl border border-gray-200 bg-white p-4 text-sm sm:grid-cols-2">
              <div className="sm:col-span-2">
                <dt className="text-gray-500">{t('academy.detail.aanbieder', { defaultValue: 'Aanbieder' })}</dt>
                <dd>
                  <Link
                    to={`/medewerker/academy/opleider/${tr.provider_id}`}
                    onClick={onClose}
                    className="inline-flex items-center gap-1.5 font-semibold text-emerald-700 underline decoration-emerald-600/40 underline-offset-2 hover:text-emerald-800"
                  >
                    <Link2 className="h-4 w-4" aria-hidden="true" />
                    {tr.organisatie_naam}
                    <span className="font-normal text-emerald-600">({t('academy.detail.bekijkProfiel', { defaultValue: 'Bekijk profiel' })})</span>
                  </Link>
                </dd>
              </div>
              {tr.duur && (
                <div>
                  <dt className="text-gray-500">{t('academy.detail.duur', { defaultValue: 'Duur' })}</dt>
                  <dd className="inline-flex items-center gap-1.5 text-gray-900">
                    <Clock className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    {tr.duur}
                  </dd>
                </div>
              )}
              {detail?.niveau && (
                <div>
                  <dt className="text-gray-500">{t('academy.detail.niveau', { defaultValue: 'Niveau' })}</dt>
                  <dd className="inline-flex items-center gap-1.5 text-gray-900">
                    <BarChart3 className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    {detail.niveau}
                  </dd>
                </div>
              )}
              {(tr.locatie_detail || tr.locaties) && (
                <div>
                  <dt className="text-gray-500">{t('academy.detail.locatie', { defaultValue: 'Locatie' })}</dt>
                  <dd className="inline-flex items-center gap-1.5 text-gray-900">
                    <MapPin className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    {tr.locatie_detail || tr.locaties}
                  </dd>
                </div>
              )}
              {tr.certificering && (
                <div>
                  <dt className="text-gray-500">{t('academy.detail.certificering', { defaultValue: 'Certificering' })}</dt>
                  <dd className="inline-flex items-center gap-1.5 text-gray-900">
                    <GraduationCap className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                    {tr.certificering}
                  </dd>
                </div>
              )}
              {detail?.docenten && (
                <div>
                  <dt className="text-gray-500">{t('academy.detail.docenten', { defaultValue: 'Docenten' })}</dt>
                  <dd className="inline-flex items-center gap-1.5 text-gray-900">
                    <Users className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    {detail.docenten}
                  </dd>
                </div>
              )}
              {detail?.accreditatie && (
                <div>
                  <dt className="text-gray-500">{t('academy.detail.accreditatie', { defaultValue: 'Accreditatie' })}</dt>
                  <dd className="text-gray-900">{detail.accreditatie}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Rechterkant (30%): transparante financiële breakdown + inkoop */}
          <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
            {geboekt ? (
              <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5 text-center" role="status">
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" aria-hidden="true" />
                <p className="mt-2 text-lg font-bold text-gray-900">{t('academy.detail.geactiveerd', { defaultValue: 'Je cursus is geactiveerd!' })}</p>
                <p className="mt-1 text-sm text-gray-600">
                  {t('academy.boekingGelukt', { defaultValue: 'Je boeking is bevestigd. Je vindt de details onder "Mijn boekingen".' })}
                </p>
                {geboekt.metAgenda && (
                  <button
                    type="button"
                    onClick={() => agendaMutation.mutate(geboekt.boekingId)}
                    disabled={agendaMutation.isPending}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B1F3A] px-4 py-3 text-sm font-semibold text-white hover:bg-[#1A3459] disabled:opacity-60"
                  >
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    {t('academy.detail.agenda', { defaultValue: 'Zet alle lesdata in mijn agenda (.ics)' })}
                  </button>
                )}
                {agendaMutation.isError && (
                  <p className="mt-2 text-xs text-red-600" role="alert">
                    {t('academy.detail.agendaFout', { defaultValue: 'Het agenda-bestand kon niet worden gedownload.' })}
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-gray-900">{t('academy.detail.prijsopbouw', { defaultValue: 'Prijsopbouw' })}</h3>
                  <dl className="mt-3 space-y-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">{t('academy.detail.marktprijs', { defaultValue: 'Normale marktprijs' })}</dt>
                      <dd className="text-gray-600">{prijs(tr.bruto_bedrag)}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="inline-flex items-center gap-1 text-emerald-700">
                        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('academy.detail.collectief', { defaultValue: 'BeLagom Collectief Voordeel ({{pct}}%)', pct: wallet?.korting_pct || 5 })}
                      </dt>
                      <dd className="whitespace-nowrap text-emerald-700">− {prijs(tr.korting_bedrag)}</dd>
                    </div>
                    <div className="flex justify-between gap-3 border-t border-gray-200 pt-2 text-base font-bold text-gray-900">
                      <dt>{t('academy.detail.activeringswaarde', { defaultValue: 'Activeringswaarde uit uw Wallet' })}</dt>
                      <dd className="whitespace-nowrap">{prijs(tr.netto_bedrag)}</dd>
                    </div>
                    {wallet?.actief && bijbetaling > 0 && (
                      <div className="space-y-1 border-t border-dashed border-gray-300 pt-2 text-xs">
                        <div className="flex justify-between text-gray-600">
                          <span>{t('academy.uitBudget', { defaultValue: 'Uit je budget' })}</span>
                          <span>{eur.format(walletDeel)}</span>
                        </div>
                        <div className="flex justify-between font-medium text-amber-700">
                          <span>{t('academy.bijbetalen', { defaultValue: 'Zelf bijbetalen (iDEAL)' })}</span>
                          <span>{eur.format(bijbetaling)}</span>
                        </div>
                      </div>
                    )}
                  </dl>
                </div>

                {heeftSessies && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-gray-900">{t('academy.detail.kiesStart', { defaultValue: 'Kies je startdatum' })}</h3>
                    {sessies.length === 0 && detailQuery.isSuccess ? (
                      <p className="text-sm text-gray-500">{t('academy.detail.geenDatum', { defaultValue: 'Wacht op nieuwe datum' })}</p>
                    ) : (
                      <ul className="space-y-2">
                        {sessies.map((s) => (
                          <SessieSlot key={s.id} sessie={s} gekozen={sessieId === s.id} onKies={() => setSessieId(s.id)} />
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {heeftSessies && detail && boekbareSessies.length === 0 && (
                  <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm">
                    <p className="text-gray-700">
                      {detail.op_wachtlijst
                        ? t('academy.detail.wachtlijstActief', { defaultValue: 'Je staat op de wachtlijst. We sturen je een seintje zodra er een nieuwe datum is.' })
                        : t('academy.detail.wachtlijstUitleg', { defaultValue: 'Alle startmomenten zijn vol of verlopen. Wil je een seintje bij een nieuwe datum?' })}
                    </p>
                    <button
                      type="button"
                      onClick={() => wachtlijstMutation.mutate(!detail.op_wachtlijst)}
                      disabled={wachtlijstMutation.isPending}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-sky-300 bg-white px-3 py-1.5 text-xs font-semibold text-sky-800 transition hover:bg-sky-100 disabled:opacity-50"
                    >
                      <CalendarPlus className="h-3.5 w-3.5" aria-hidden="true" />
                      {detail.op_wachtlijst
                        ? t('academy.detail.wachtlijstAf', { defaultValue: 'Haal mij van de wachtlijst' })
                        : t('academy.detail.wachtlijstOp', { defaultValue: 'Houd mij op de hoogte van nieuwe data' })}
                    </button>
                    {wachtlijstMutation.isError && (
                      <p className="mt-1 text-xs text-red-600" role="alert">
                        {foutMelding(wachtlijstMutation.error, t('academy.detail.wachtlijstFout', { defaultValue: 'Dat is niet gelukt. Probeer het opnieuw.' }))}
                      </p>
                    )}
                  </div>
                )}

                {wkrGrendel && (
                  <div className="flex gap-2 rounded-xl border p-3 text-sm" style={{ borderColor: `${WKR_ORANJE}80`, backgroundColor: `${WKR_ORANJE}14`, color: '#9A3412' }} role="alert">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <p>
                      {t('academy.detail.wkrGrendel', {
                        defaultValue:
                          'Let op: het fiscale hobby-budget van je werkgever voor dit kalenderjaar is bereikt. Je kunt deze cursus wel activeren, maar betaalt het volledige bedrag zelf via iDEAL.',
                      })}
                    </p>
                  </div>
                )}

                {boekMutation.isError && !inschrijven && (
                  <p className="text-sm text-red-600" role="alert">
                    {foutMelding(boekMutation.error, t('academy.boekenMislukt', { defaultValue: 'Boeken is niet gelukt. Probeer het opnieuw.' }))}
                  </p>
                )}

                <button
                  type="button"
                  onClick={() => setInschrijven(true)}
                  disabled={!kanBoeken || boekMutation.isPending}
                  className="w-full rounded-xl px-4 py-3.5 text-sm font-bold text-white shadow-lg transition enabled:hover:brightness-110 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none"
                  style={kanBoeken ? { backgroundColor: '#16a34a', boxShadow: '0 10px 30px -10px rgba(22,163,74,.7)' } : undefined}
                >
                  {boekMutation.isPending ? '…' : knopTekst}
                </button>

                {wallet?.actief && bijbetaling > 0 && (
                  <p className="text-xs leading-relaxed text-gray-500">
                    {t('academy.privacyBijbetaling', {
                      defaultValue:
                        'Je werkgever ziet alleen je registratienummer en de bijdrage uit het budget. Je bankrekening en het feit dat je bijbetaalt blijven onzichtbaar.',
                    })}
                  </p>
                )}
                {!wallet?.actief && (
                  <p className="text-xs text-gray-500">
                    {t('academy.nietActiefUitleg', {
                      defaultValue: 'Je werkgever heeft de Academy nog niet geactiveerd. Je kunt alvast rondkijken; boeken kan zodra het budget is vrijgegeven.',
                    })}
                  </p>
                )}
              </>
            )}
          </aside>
        </div>
      </div>

      {inschrijven && !geboekt && (
        <AcademyInschrijfModal
          titel={tr.titel}
          opleider={tr.organisatie_naam}
          knopTekst={knopTekst}
          bezig={boekMutation.isPending}
          fout={boekMutation.isError ? foutMelding(boekMutation.error, t('academy.boekenMislukt', { defaultValue: 'Boeken is niet gelukt. Probeer het opnieuw.' })) : null}
          onSubmit={(gegevens) => boekMutation.mutate(gegevens)}
          onClose={() => {
            if (boekMutation.isPending) return;
            boekMutation.reset();
            setInschrijven(false);
          }}
        />
      )}
    </div>
  );
};

export default AcademyDetailModal;
